import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, ChevronLeft, Users, FolderOpen, Image as ImageIcon,
  Film, Pencil, Check, X, ChevronDown, ChevronRight, ExternalLink,
  Library as LibraryIcon, User, Copy,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  getAdminUsers, adminUpdateUsername,
  getAdminUserProjects, getAdminUserLibrary,
  duplicateProject,
} from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatDuration } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------------------------------------------------------------------------
// Inline username editor
// ---------------------------------------------------------------------------
function UsernameCell({ userId, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const commit = async () => {
    const t = draft.trim();
    if (!t) { setEditing(false); setDraft(value || ''); return; }
    try {
      await adminUpdateUsername(userId, t);
      onSaved(t);
      toast.success('Pseudo mis à jour');
    } catch { toast.error('Échec'); setDraft(value || ''); }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
          onBlur={commit}
          className="h-7 text-xs px-2 w-36"
        />
        <button type="button" className="p-0.5 text-primary" onClick={commit}><Check className="w-3.5 h-3.5" /></button>
        <button type="button" className="p-0.5 text-muted-foreground" onClick={() => { setEditing(false); setDraft(value || ''); }}><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <button
      type="button"
      className="group/un flex items-center gap-1 text-left hover:text-primary transition-colors"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="Modifier le pseudo"
    >
      <span className="font-medium">{value || <span className="text-muted-foreground/50 italic">Sans pseudo</span>}</span>
      <Pencil className="w-3 h-3 opacity-0 group-hover/un:opacity-60 shrink-0" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Projects panel (expandable)
// ---------------------------------------------------------------------------
function ProjectsPanel({ userId }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(null);

  useEffect(() => {
    getAdminUserProjects(userId)
      .then(setProjects)
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleDuplicate = async (project, e) => {
    e.stopPropagation();
    setDuplicating(project.id);
    try {
      const copy = await duplicateProject(project.id);
      toast.success(`« ${copy.title} » copié dans vos projets`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de la copie');
    } finally { setDuplicating(null); }
  };

  if (loading) return <div className="p-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  if (!projects?.length) return <p className="p-3 text-xs text-muted-foreground/60">Aucun projet</p>;

  return (
    <div className="divide-y divide-border/50">
      {projects.map((p) => (
        <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/30 transition-colors">
          <div className="w-16 h-9 rounded bg-secondary/40 overflow-hidden shrink-0">
            {p.shots?.[0]?.frame_path
              ? <img src={API_URL + p.shots[0].frame_path} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
              : <div className="w-full h-full flex items-center justify-center"><Film className="w-3.5 h-3.5 text-muted-foreground/20" /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">{p.title}</p>
            <p className="text-[11px] text-muted-foreground">
              {p.shots?.length || 0} plans
              {p.shots?.length > 0 && <span className="ml-1 opacity-60">{formatDuration(p.shots.reduce((a, s) => a + (s.duration || 0), 0))}</span>}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              onClick={(e) => { e.stopPropagation(); navigate(`/project/${p.id}`); }}
              title="Ouvrir le projet"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
              onClick={(e) => handleDuplicate(p, e)}
              disabled={duplicating === p.id}
              title="Copier dans mes projets"
            >
              {duplicating === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library panel (expandable)
// ---------------------------------------------------------------------------
function LibraryPanel({ userId }) {
  const [lib, setLib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  useEffect(() => {
    getAdminUserLibrary(userId)
      .then(setLib)
      .catch(() => setLib({ folders: [], items: [] }))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="p-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  if (!lib?.folders?.length && !lib?.items?.length) return <p className="p-3 text-xs text-muted-foreground/60">Bibliothèque vide</p>;

  const toggleFolder = (id) => setExpandedFolders((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const renderItem = (item) => {
    const src = item.canonical_url?.startsWith('http') ? item.canonical_url : API_URL + (item.canonical_url || '');
    return (
      <div key={item.id} className="relative aspect-video rounded overflow-hidden bg-secondary/30 border border-border/40" title={item.name}>
        {src && <img src={src} alt={item.name} className="w-full h-full object-cover" />}
      </div>
    );
  };

  const renderFolder = (folder, depth = 0) => {
    const folderItems = lib.items.filter((it) => it.folder_id === folder.id);
    const subfolders = lib.folders.filter((f) => f.parent_id === folder.id);
    const expanded = expandedFolders.has(folder.id);
    const total = folderItems.length + subfolders.length;
    return (
      <div key={folder.id}>
        <button
          type="button"
          className="w-full flex items-center gap-1.5 px-4 py-1.5 text-xs hover:bg-secondary/30 transition-colors text-left"
          style={{ paddingLeft: `${16 + depth * 12}px` }}
          onClick={() => toggleFolder(folder.id)}
        >
          {expanded ? <ChevronDown className="w-3 h-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />}
          <FolderOpen className="w-3 h-3 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">{folder.name}</span>
          <span className="ml-auto text-[10px] text-muted-foreground/60">{total}</span>
        </button>
        {expanded && (
          <div>
            {subfolders.map((f) => renderFolder(f, depth + 1))}
            {folderItems.length > 0 && (
              <div className="grid grid-cols-4 gap-1 px-4 pb-2" style={{ paddingLeft: `${16 + (depth + 1) * 12}px` }}>
                {folderItems.map(renderItem)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const rootFolders = lib.folders.filter((f) => !f.parent_id);
  const rootItems = lib.items.filter((it) => !it.folder_id);

  return (
    <div className="py-1">
      {rootFolders.map((f) => renderFolder(f))}
      {rootItems.length > 0 && (
        <div className="grid grid-cols-4 gap-1 px-4 py-1">
          {rootItems.map(renderItem)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// User row (table row with expand)
// ---------------------------------------------------------------------------
function UserRow({ user: u, onUsernameChanged }) {
  const [expanded, setExpanded] = useState(null); // null | 'projects' | 'library'

  const toggle = (panel) => setExpanded((prev) => prev === panel ? null : panel);

  return (
    <>
      <tr className="border-b border-border hover:bg-secondary/20 transition-colors cursor-default">
        {/* Avatar + username */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
              {u.picture
                ? <img src={u.picture} alt="" className="w-full h-full object-cover" />
                : <User className="w-3.5 h-3.5 text-muted-foreground" />}
            </div>
            <UsernameCell
              userId={u.user_id}
              value={u.username}
              onSaved={(v) => onUsernameChanged(u.user_id, v)}
            />
          </div>
        </td>
        {/* Email */}
        <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px] truncate">{u.email}</td>
        {/* Projects */}
        <td className="px-4 py-3">
          <button
            type="button"
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${expanded === 'projects' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/60 text-foreground'}`}
            onClick={() => toggle('projects')}
          >
            <Film className="w-3 h-3" />
            {u.project_count}
            {expanded === 'projects' ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </td>
        {/* Library */}
        <td className="px-4 py-3">
          <button
            type="button"
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${expanded === 'library' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/60 text-foreground'}`}
            onClick={() => toggle('library')}
          >
            <LibraryIcon className="w-3 h-3" />
            {u.folder_count}f · {u.item_count}i
            {expanded === 'library' ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </td>
        {/* Images générées */}
        <td className="px-4 py-3">
          <span className="flex items-center gap-1 text-xs">
            <ImageIcon className="w-3 h-3 text-muted-foreground" />
            {u.generation_count || 0}
          </span>
        </td>
        {/* Inscription */}
        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
          {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
        </td>
      </tr>
      {/* Expandable panels */}
      {expanded && (
        <tr className="border-b border-border bg-secondary/10">
          <td colSpan={6} className="p-0">
            <div className="border-l-2 border-primary/30 ml-10 my-1 rounded-sm overflow-hidden bg-background">
              {expanded === 'projects' && <ProjectsPanel userId={u.user_id} />}
              {expanded === 'library' && <LibraryPanel userId={u.user_id} />}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await getAdminUsers()); }
    catch { toast.error('Échec du chargement des utilisateurs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const handleUsernameChanged = (userId, newUsername) => {
    setUsers((prev) => prev.map((u) => u.user_id === userId ? { ...u, username: newUsername } : u));
  };

  const filtered = search.trim()
    ? users.filter((u) =>
        (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.username || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.name || '').toLowerCase().includes(search.toLowerCase()))
    : users;

  const totalGenerations = users.reduce((a, u) => a + (u.generation_count || 0), 0);

  if (authLoading || !user) {
    return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <Layout title="Supervision">
      <div className="h-full overflow-y-auto">
        <div className="max-w-7xl mx-auto p-8 space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2 transition-colors"
                onClick={() => navigate('/admin')}
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Paramètres admin
              </button>
              <h1 className="text-xl font-semibold tracking-tight">Supervision utilisateurs</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Vue d'ensemble des comptes, projets et générations.</p>
            </div>
          </div>

          {/* Stats bar */}
          {!loading && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Utilisateurs', value: users.length, icon: Users },
                { label: 'Images générées', value: totalGenerations, icon: ImageIcon },
                { label: 'Projets (admin)', value: users.find((u) => u.email?.toLowerCase() === (user.email || '').toLowerCase())?.project_count ?? '—', icon: Film },
                { label: 'Items biblio (admin)', value: users.find((u) => u.email?.toLowerCase() === (user.email || '').toLowerCase())?.item_count ?? '—', icon: LibraryIcon },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="bg-card border border-border rounded-xl p-4 space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </div>
                  <p className="text-2xl font-semibold tabular-nums">{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="flex items-center gap-3">
            <input
              type="search"
              placeholder="Filtrer par email, pseudo ou nom…"
              className="h-9 rounded-lg bg-card border border-input px-3 text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all w-full max-w-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Actualiser'}
            </Button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Utilisateur</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Projets</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bibliothèque</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Images</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Inscription</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                        {search ? 'Aucun résultat pour cette recherche' : 'Aucun utilisateur'}
                      </td>
                    </tr>
                  ) : filtered.map((u) => (
                    <UserRow
                      key={u.user_id}
                      user={u}
                      onUsernameChanged={handleUsernameChanged}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
